import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useIsLoggedIn, useSupportWidgetConfig } from "@/hooks";
import { useUser } from "@/hooks/useUser";
import { useCookieConsent } from "@/lib/cookieConsent";
import { configureSupportWidget, loadSupportWidget, setSupportPlayer } from "@/lib/supportWidget";
import { isTelegramMiniApp } from "@/lib/telegram";
import { baseLanguage } from "@/locales/configLang";
import { getSupportWidgetIdentity } from "@/services";

const IDLE_TIMEOUT_MS = 4000;

const whenIdle = (run: () => void): (() => void) => {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
  }
  const timer = setTimeout(run, IDLE_TIMEOUT_MS);
  return () => clearTimeout(timer);
};

/**
 * Loads the configured live chat. The launcher appears on its own only after the player allowed
 * analytics cookies (the chat sets third-party cookies); without that, the chat loads when the
 * player asks for it (openSupport), which is a service they requested.
 */
const SupportWidget = () => {
  const { i18n } = useTranslation();
  const consent = useCookieConsent();
  const user = useUser();
  const isLoggedIn = useIsLoggedIn();
  const { data } = useSupportWidgetConfig();
  const enabled = !!data && data.provider !== "none";

  useEffect(() => {
    if (!data) return;
    // Inside the Telegram Mini App the floating launcher covers the bottom bar; the chat still opens on request.
    // Crisp and Intercom know "es", not "es-AR".
    configureSupportWidget(data, {
      language: baseLanguage(i18n.language),
      hideLauncher: isTelegramMiniApp(),
    });
  }, [data, i18n.language]);

  useEffect(() => {
    if (!enabled || !consent?.analytics) return;
    return whenIdle(loadSupportWidget);
  }, [enabled, consent?.analytics]);

  const userId = user?._id ? String(user._id) : "";
  const username = user?.username ?? "";
  const email = user?.email && !/\.invalid$/i.test(user.email) ? user.email : "";
  const language = baseLanguage(user?.language || i18n.language);

  useEffect(() => {
    if (!enabled) return;
    if (!isLoggedIn || !userId) {
      setSupportPlayer(null);
      return;
    }
    let cancelled = false;
    (async () => {
      let signed: { userHash?: string; emailSignature?: string } = {};
      if (data?.identityVerification) {
        try {
          signed = await getSupportWidgetIdentity();
        } catch {
          // Unsigned: the provider shows the player as unverified.
        }
      }
      if (!cancelled) {
        setSupportPlayer({ userId, username, email, language, ...signed });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, data?.identityVerification, isLoggedIn, userId, username, email, language]);

  return null;
};

export default SupportWidget;
